import React, { Suspense, lazy, useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import AppHeader from "./components/AppHeader";
import AppBootScreen from "./components/AppBootScreen";
import BottomNav from "./components/BottomNav";
import MechanicsSkeleton from "./components/MechanicsSkeleton";
import MarketDocumentMetadata from "./components/MarketDocumentMetadata";
import WalletConnectPrompt from "./components/WalletConnectPrompt";
import useTelegram from "./hooks/useTelegram";
import useTelegramBackButton from "./hooks/useTelegramBackButton";
import useUserAuth from "./hooks/useUserAuth";
import { canAccessEconomics, hasAdminAccess, isConfiguredAdmin, isPreviewFeatureAdmin } from "./utils/adminAccess";
import { shouldShowBottomNav } from "./utils/bottomNavRoutes";
import { useWalletConnect } from "./WalletConnectContext";

const CraftSectionRoute = lazy(() => import("./components/CraftSection"));
const CraftingPublicSectionRoute = lazy(() => import("./components/CraftingPublicSection"));
const ClaimPageRoute = lazy(() => import("./pages/ClaimPage"));
const EconomicsPageRoute = lazy(() => import("./pages/EconomicsPage"));
const ReferralPageRoute = lazy(() => import("./pages/ReferralPage"));
const PulsarPageRoute = lazy(() => import("./pages/PulsarPage"));
const ProjectWalletPageRoute = lazy(() => import("./pages/ProjectWalletPage"));
const DashboardPageRoute = lazy(() => import("./components/DashboardPage"));
const GamesSectionRoute = lazy(() => import("./components/GamesSection"));
const HomeSectionRoute = lazy(() => import("./components/HomeSection"));
const HotRpcDiagnosticPageRoute = lazy(() => import("./pages/HotRpcDiagnosticPage"));
const LeaderboardSectionRoute = lazy(() => import("./components/LeaderboardSection"));
const MarketSectionRoute = lazy(() => import("./components/MarketSection"));
const MechanicsSectionRoute = lazy(() => import("./components/MechanicsSection"));
const StakingSectionRoute = lazy(() => import("./components/StakingSection"));
const TamagotchiIframeRoute = lazy(() => import("./components/TamagotchiIframe"));
const WalletSectionRoute = lazy(() => import("./components/WalletSection"));
const HousePageRoute = lazy(() => import("./pages/HousePage"));
const MinePageRoute = lazy(() => import("./pages/MinePage"));
const NoraPageRoute = lazy(() => import("./pages/NoraPage"));
const UNIFIED_WALLET_PROMPT_PATHS = new Set(["/wallet", "/house", "/mine"]);
const HOT_RPC_DIAGNOSTIC_PATH = "/support/hot-rpc";
const ECONOMICS_PATH = "/economics";
const PROJECT_WALLET_PATH = "/project-wallet";
const PROJECT_WALLET_VIEWER = "kvp_vs.tg";

function RouteFallback({ pathname }) {
  if (pathname === "/mechanics") {
    return <MechanicsSkeleton />;
  }

  return <AppBootScreen pathname={pathname} showBottomNavPlaceholder={false} />;
}

function EmbeddedMarketRoute() {
  return (
    <>
      <MarketDocumentMetadata embedded />
      <MarketSectionRoute />
    </>
  );
}

export default function WebAppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isTelegram, tgWebApp } = useTelegram();
  const { selector, accountId } = useWalletConnect() || {};
  const isConfigAdmin = isConfiguredAdmin(accountId);
  const isEconomicsPath = location.pathname === ECONOMICS_PATH;
  const shouldLoadProtectedUser = isEconomicsPath || (location.pathname === "/claim" && !isConfigAdmin);
  const { user, isAdmin: isBackendAdmin, loading: userAuthLoading } = useUserAuth({
    enabled: shouldLoadProtectedUser,
  });
  const isPreviewAdmin = isPreviewFeatureAdmin(accountId);
  const isAdmin = hasAdminAccess(accountId, user, isBackendAdmin);
  const isHiddenClaimPath = location.pathname === "/claim";
  const isReferralPath = location.pathname === "/referral";
  const shouldShowUnifiedWalletPrompt =
    (!selector || !accountId) && UNIFIED_WALLET_PROMPT_PATHS.has(location.pathname);
  const showAppHeader =
    !isHiddenClaimPath && !isReferralPath && !location.pathname.startsWith("/wallet") && !shouldShowUnifiedWalletPrompt;
  const showBottomNav = shouldShowBottomNav(location.pathname);
  const renderWalletProtectedRoute = (element) =>
    selector && accountId ? element : <WalletConnectPrompt />;
  const renderPreviewProtectedRoute = (element) => {
    if (!selector || !accountId) return <WalletConnectPrompt />;
    return isPreviewAdmin ? element : <Navigate to="/home" replace />;
  };
  const renderProjectWalletProtectedRoute = (element) => {
    if (!selector || !accountId) return <WalletConnectPrompt />;
    return String(accountId).trim().toLowerCase() === PROJECT_WALLET_VIEWER
      ? element
      : <Navigate to="/home" replace />;
  };
  const renderEconomicsProtectedRoute = (element) => {
    if (!selector || !accountId) return <WalletConnectPrompt />;
    if (userAuthLoading || user?.near_wallet !== accountId) {
      return <AppBootScreen pathname={location.pathname} showBottomNavPlaceholder={false} />;
    }
    return canAccessEconomics(accountId, user, isBackendAdmin)
      ? element
      : <Navigate to="/home" replace />;
  };
  const renderClaimProtectedRoute = (element) => {
    if (!selector || !accountId) return <WalletConnectPrompt />;
    if (isConfigAdmin || isBackendAdmin || user?.has_referrer) return element;
    if (userAuthLoading || user?.near_wallet !== accountId) {
      return <AppBootScreen pathname={location.pathname} showBottomNavPlaceholder={false} />;
    }
    return <Navigate to="/home" replace />;
  };
  const userAuthPending = Boolean(accountId) && !isConfigAdmin &&
    (userAuthLoading || user?.near_wallet !== accountId);

  useEffect(() => {
    if (!tgWebApp) return undefined;

    try {
      tgWebApp.expand();
      tgWebApp.disableVerticalSwipes?.();
      document.documentElement.classList.add("tg-app");
    } catch (error) {
      console.warn("[TG WebApp] swipe disable failed:", error);
    }

    return () => {
      try {
        tgWebApp.enableVerticalSwipes?.();
        document.documentElement.classList.remove("tg-app");
      } catch {
        return undefined;
      }
    };
  }, [tgWebApp]);

  const isHome = location.pathname === "/home" || location.pathname === "/";
  useTelegramBackButton(
    isTelegram && !isHome,
    () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/home");
      }
    },
    { priority: -1 }
  );

  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      const main = document.querySelector(".neu-main");
      if (main) main.scrollTop = 0;
    } catch {
      return;
    }
  }, [location.pathname]);

  const handleNavChange = (next) => {
    switch (next) {
      case "home":
        navigate("/home");
        break;
      case "mechanics":
        navigate("/mechanics");
        break;
      case "staking":
        navigate("/staking");
        break;
      case "leaderboard":
        navigate("/leaderboard");
        break;
      case "crafting":
        navigate("/crafting");
        break;
      case "craft":
        navigate("/craft");
        break;
      case "market":
        navigate("/market");
        break;
      case "wallet":
        navigate("/wallet");
        break;
      case "dashboard":
        navigate("/dashboard");
        break;
      default:
        navigate("/home");
    }
  };

  return (
    <>
      {showAppHeader && <AppHeader />}
      <main
        className={`webapp-main${showBottomNav ? " webapp-main--with-bottom-nav" : ""}`}
        style={{ minHeight: "calc(100vh - 120px)", paddingInline: "2px" }}
      >
        <Suspense fallback={<RouteFallback pathname={location.pathname} />}>
          <Routes>
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<HomeSectionRoute />} />
            <Route path="/pulsar" element={<PulsarPageRoute />} />
            <Route path="/claim" element={renderClaimProtectedRoute(<ClaimPageRoute />)} />
            <Route path="/economics" element={renderEconomicsProtectedRoute(<EconomicsPageRoute />)} />
            <Route path={PROJECT_WALLET_PATH} element={renderProjectWalletProtectedRoute(<ProjectWalletPageRoute />)} />
            <Route path="/referral" element={renderPreviewProtectedRoute(<ReferralPageRoute />)} />
            <Route path="/mechanics" element={<MechanicsSectionRoute />} />
            <Route
              path="/house"
              element={renderWalletProtectedRoute(<HousePageRoute />)}
            />
            <Route
              path="/mine"
              element={renderWalletProtectedRoute(<MinePageRoute />)}
            />
            <Route path="/games" element={<GamesSectionRoute />} />
            <Route
              path="/craft"
              element={isConfigAdmin ? <CraftingPublicSectionRoute /> : <Navigate to="/home" replace />}
            />
            <Route
              path="/crafting"
              element={(
                <CraftSectionRoute
                  goToSection={handleNavChange}
                  isAdmin={isAdmin}
                  userAuthPending={userAuthPending}
                />
              )}
            />
            <Route
              path="/market"
              element={<EmbeddedMarketRoute />}
            />
            <Route
              path="/wallet"
              element={renderWalletProtectedRoute(
                <WalletSectionRoute accountId={accountId} selector={selector} />
              )}
            />
            <Route path="/user" element={<DashboardPageRoute />} />
            <Route path="/staking" element={<StakingSectionRoute />} />
            <Route path="/leaderboard" element={<LeaderboardSectionRoute pageBackground />} />
            <Route path="/nora" element={<NoraPageRoute />} />
            <Route path="/tamagotchi" element={<TamagotchiIframeRoute />} />
            <Route path="/dashboard" element={<DashboardPageRoute />} />
            <Route
              path={HOT_RPC_DIAGNOSTIC_PATH}
              element={renderWalletProtectedRoute(<HotRpcDiagnosticPageRoute />)}
            />
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        </Suspense>
      </main>
      {showBottomNav && <BottomNav showReferral={isPreviewAdmin} isAdmin={isConfigAdmin} />}
    </>
  );
}
